import React from 'react';
import { useState, useEffect ,useMemo} from "react";
import Swal from "sweetalert2";
import { Row, Col, Button } from "reactstrap";
import Sidebar from '../../Sidebar';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import TableRowsCubeTestReport from "./TableRowsCubeTestReport";
import FloatingControls from '../../components/FloatingControls';
import PremiumPageHeader from '../../components/PremiumPageHeader';
import LoadingOverlay from '../../components/LoadingOverlay';
import { getUserAllowedPlants } from '../../services/PlantServices';
import { getCubeTestReportNumber, createCubeTestReport, getPendingCubeTestReports } from '../../services/CubeTestReportServices';
import NumberSetting from '../../components/NumberSetting';
import { displayError } from '../../helpers';
import { getDisplayDate } from '../../utils/DateUtils';
import ReactTablePopUpSubmitWrapper from "../../components/ReactTablePopUpSubmitWrapper";
import CubeTestReportSelectionBanner from './CubeTestReportSelectionBanner';
import CubeTestReportAgePanel from './CubeTestReportAgePanel';
import {
  buildPendingAgeRow,
  buildPendingListTitle,
  buildCsrRowsFromAgeSetup,
  buildCubeIdFromSelection,
  syncCubeIdsForAgeRows,
  resolveNextAgeDays,
  applyAgeRowFieldChange,
  castingDateInputFromApi,
  decoratePendingRecords,
  getInitialCubeTestReportFormState,
  getPendingColumns,
  getPendingQueryRange,
  mapAllowedPlants,
  pickDefaultPlantId,
  reportDateFromCastingAndAge,
  resolveCuredPlaceFromAgeRows,
  CUBE_ID_COLUMN_MIN_WIDTH,
} from './cubeTestReportDefaults';

function CubeTestReport() 
{
  const initialFormState = getInitialCubeTestReportFormState();
  const [category] = useCookies(['myCategory']);
  const [superuser] = useCookies(['mySuperuser']);
  const [cookies] = useCookies(['myToken']);
  const [inputs, setInputs] = useState(initialFormState);
  const [inProgress, setInProgress] = useState(false);
  const [dcGroup,setDcGroup]=useState([]);
  const [SelectedDcGroup,setSelectedDcGroup]=useState({});
  const [plants, setPlants] = React.useState([]);
  const [csrRows, setCsrRows] = useState([]);
  const [rowsData, setRowsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [pendingModalTitle, setPendingModalTitle] = useState('');
  const [pendingAgeDays, setPendingAgeDays] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pendingOpenedRef = React.useRef(false);
  
  const applyDcGroupToForm = (selectedDcGroup, ageDays) => {
    const castingDt = castingDateInputFromApi(selectedDcGroup.delivery_challan_date);
    const reportDate = reportDateFromCastingAndAge(castingDt, ageDays);
    const cubeId = buildCubeIdFromSelection(selectedDcGroup, ageDays, castingDt);
    setSelectedDcGroup(selectedDcGroup);
    setInputs((values) => ({
      ...values,
      dc_group_id: selectedDcGroup.id,
      casting_dt: castingDt,
      csr_date: reportDate || values.csr_date,
      pending_age_days: ageDays,
      cured_place: values.cured_place || 'plant',
    }));
    setRowsData(syncCubeIdsForAgeRows(
      [buildPendingAgeRow(ageDays, cubeId, 'plant')],
      selectedDcGroup,
      castingDt,
    ));
    if (inputs.plant_id && reportDate) {
      loadReportNumberForPlant(inputs.plant_id, reportDate);
    }
  };

  const openPendingList = (ageDays) => {
    if (!inputs.plant_id) {
      Swal.fire('Please choose plant location first.', '', 'info');
      return;
    }
    const queryRange = getPendingQueryRange(ageDays);
    setIsLoading(true);
    setPendingAgeDays(ageDays);
    getPendingCubeTestReports(cookies, inputs.plant_id, ageDays, {
      fromDate: queryRange.fromDate.format('DD-MM-YYYY'),
      uptoDate: queryRange.uptoDate.format('DD-MM-YYYY'),
    })
      .then((response) => {
        const tableData = decoratePendingRecords(response.pending_list);
        setDcGroup(tableData);
        setPendingModalTitle(buildPendingListTitle(ageDays, queryRange.fromDate, queryRange.uptoDate));
        setShowPendingModal(true);
        setIsLoading(false);
        if (!tableData.length) {
          Swal.fire(
            `No pending ${ageDays}-day records found for this plant in the last 90 days.`,
            '',
            'info',
          );
        }
      })
      .catch((error) => {
        setIsLoading(false);
        displayError(error.response?.data, ' Failed');
      });
  };

  React.useEffect(() => {
    const pendingAge = parseInt(searchParams.get('pendingAge') || '', 10);
    if ((pendingAge === 7 || pendingAge === 28) && inputs.plant_id && !pendingOpenedRef.current) {
      pendingOpenedRef.current = true;
      openPendingList(pendingAge);
    }
  }, [searchParams, inputs.plant_id]);

  const loadReportNumberForPlant = (plantId, reportDate) => {
    if (!plantId) {
      setInputs((values) => ({
        ...values,
        csr_no: '',
        prefix: '',
        csr_num_prefix: '',
      }));
      return;
    }
    const reportDateParam = reportDate ? getDisplayDate(reportDate) : undefined;
    getCubeTestReportNumber(cookies, plantId, reportDateParam)
      .then((response) => {
        setIsLoading(false);
        setInputs((values) => ({
          ...values,
          plant_id: plantId,
          csr_num_prefix: `${response.prefix}${response.cube_test_report_no}`,
          csr_no: response.cube_test_report_no,
          prefix: response.prefix,
        }));
      })
      .catch((error) => {
        setIsLoading(false);
        displayError(error.response?.data, ' Failed');
      });
  };

  React.useEffect(() => {
    getUserAllowedPlants(cookies)
      .then((plantList) => {
        const plantOptions = mapAllowedPlants(plantList);
        setPlants(plantOptions);
        const plantIdFromUrl = searchParams.get('plantId');
        const defaultPlantId = plantIdFromUrl || pickDefaultPlantId(plantOptions);
        if (defaultPlantId) {
          loadReportNumberForPlant(defaultPlantId);
        }
      });
  }, [cookies, searchParams]);

  const generateCubeRows = () => {
    if (!inputs.casting_dt || !SelectedDcGroup.grade_number) {
      Swal.fire('Please choose casting date and delivery challan from pending list.', '', 'info');
      return;
    }

    const newCsrRows = buildCsrRowsFromAgeSetup(rowsData, inputs.casting_dt, 'create');
    if (!newCsrRows.length) {
      Swal.fire('Please enter at least one mould count greater than 0.', '', 'info');
      return;
    }
    if (!resolveCuredPlaceFromAgeRows(rowsData)) {
      Swal.fire('Please select mould casting area for each age row.', '', 'info');
      return;
    }

    setCsrRows(newCsrRows);
    setInputs((values) => ({
      ...values,
      no_of_moulds: newCsrRows.length,
      cured_place: resolveCuredPlaceFromAgeRows(rowsData, values.cured_place),
    }));
  };

  const agePanelVisible = Boolean(inputs.casting_dt && SelectedDcGroup.grade_number);

  React.useEffect(() => {
    if (!SelectedDcGroup?.customer_name || !rowsData.length) {
      return;
    }
    setRowsData((current) => syncCubeIdsForAgeRows(current, SelectedDcGroup, inputs.casting_dt));
  }, [SelectedDcGroup?.customer_name, SelectedDcGroup?.product_name, inputs.casting_dt]);
      
  const pendingColumns = useMemo(() => getPendingColumns(true), []);

  const deleteTableRows = (index)=>{
    const rows = [...rowsData];
    rows.splice(index, 1);
    setRowsData(rows);
  }

  const handletableChange = (index, evnt) => {
    const { name, value } = evnt.target;
    setRowsData(applyAgeRowFieldChange(rowsData, index, name, value, SelectedDcGroup, inputs.casting_dt));
  };

  const handlePendingRowSelect = (value) => {
    const selected = dcGroup.find((obj) => obj.id == value);
    if (!selected) {
      return;
    }
    if (selected.pending_type === 'update' && selected.csr_id) {
      setShowPendingModal(false);
      navigate(`/CubeTestReportEdit/${selected.csr_id}/?pendingAge=${pendingAgeDays || 28}`);
      return;
    }
    applyDcGroupToForm(selected, pendingAgeDays);
    setShowPendingModal(false);
  }
  
  const addTableRows = () => {
    const pendingAge = inputs.pending_age_days || pendingAgeDays || 7;
    const nextAge = resolveNextAgeDays(rowsData, pendingAge);
    const defaultCubeId = buildCubeIdFromSelection(SelectedDcGroup, nextAge, inputs.casting_dt);
    const defaultCuredPlace = rowsData[0]?.cured_place || inputs.cured_place || 'plant';
    setRowsData([
      ...syncCubeIdsForAgeRows(rowsData, SelectedDcGroup, inputs.casting_dt),
      buildPendingAgeRow(nextAge, defaultCubeId, defaultCuredPlace),
    ]);
  };

  const handleChangeCsr = (index, evnt)=>{
    const { name, value } = evnt.target;
    const CsrRowsInput = [...csrRows];
    CsrRowsInput[index][name] = value;
    if (name==='act_load' || name === 'cus_load'){
      const strength=(value/22.5).toFixed(2);
      const age =parseInt(CsrRowsInput[index]['age']);
      const no_of_rows =CsrRowsInput[index]['no_of_rows'];
      var total = parseFloat(strength);
      var per_csr = age/(4.7+(0.832*age))*100;
      var grade =SelectedDcGroup.grade_number;
      // console.log(grade+"grade");
      var cube_strength = (parseInt(grade)*per_csr/100).toFixed(2);
      CsrRowsInput[index]['ref_cube_strength']=cube_strength;
      CsrRowsInput.map((row,row_index)=>{
        // console.log(JSON.stringify(row)+"row"+row_index+"row_index");
        if((parseInt(row['age']) === age) && (row_index != index)){
          if (name==='act_load'){
          total+= parseFloat(row['act_strength'])
          }
          else{
            total+= parseFloat(row['cus_strength'])
          }
        }
      })
      var average = (total/no_of_rows).toFixed(2);
      CsrRowsInput.map((row_1,row_index_1)=>{
        // console.log(JSON.stringify(row)+"row"+row_index+"row_index");
        if(parseInt(row_1['age']) === age){
          if (name==='act_load'){
            CsrRowsInput[index]['act_strength'] = strength;
            CsrRowsInput[row_index_1]['act_average'] = average;
            CsrRowsInput[row_index_1]['act_difference']= (parseFloat(average)-CsrRowsInput[row_index_1]['act_strength']).toFixed(2);
            CsrRowsInput[index]['cus_load'] = value;
            CsrRowsInput[index]['cus_strength'] = strength;
            CsrRowsInput[row_index_1]['cus_average'] = average;
            CsrRowsInput[row_index_1]['cus_difference']= (parseFloat(average)-CsrRowsInput[row_index_1]['act_strength']).toFixed(2);
            if(parseFloat(strength)>=parseFloat(cube_strength)){
              CsrRowsInput[index]['act_strength_result']= 'PASS'
              CsrRowsInput[index]['cus_strength_result']= 'PASS'
            }
            else{
              CsrRowsInput[index]['act_strength_result']= 'FAIL'
              CsrRowsInput[index]['cus_strength_result']= 'FAIL'
            }
            if(parseFloat(average)>=parseFloat(cube_strength)){
              CsrRowsInput[row_index_1]['act_result']= 'PASS'
              CsrRowsInput[row_index_1]['cus_result']= 'PASS'
            }
            else{
              CsrRowsInput[row_index_1]['act_result']= 'FAIL'
              CsrRowsInput[row_index_1]['cus_result']= 'FAIL'
            }
        }
        else{
          CsrRowsInput[index]['cus_strength'] = strength;
          CsrRowsInput[row_index_1]['cus_average'] = average;
          CsrRowsInput[row_index_1]['cus_difference']= (parseFloat(average)-CsrRowsInput[row_index_1]['cus_strength']).toFixed(2);
          if(parseFloat(strength)>=parseFloat(cube_strength)){
            CsrRowsInput[index]['cus_strength_result']= 'PASS'
          }
          else{
            CsrRowsInput[index]['cus_strength_result']= 'FAIL'
          }
          if(parseFloat(average)>=parseFloat(cube_strength)){
            CsrRowsInput[row_index_1]['cus_result']= 'PASS'
          }
          else{
            CsrRowsInput[row_index_1]['cus_result']= 'FAIL'
          }
        }
        }
      })
      // console.log(total+"total");
    }
    else if(name==='act_weight'){
      CsrRowsInput[index]['cus_weight'] = value;
    }
    // console.log(JSON.stringify(CsrRowsInput)+"CsrRowsInput");
    setCsrRows(CsrRowsInput);
  }

  const handleSubmit=(event)=> {
    event.preventDefault();
    console.log(JSON.stringify(inputs)+"inputs submit");
    console.log(JSON.stringify(csrRows)+csrRows.length+"csrRows");
    if(inputs.no_of_moulds > 0){
      Swal.fire({title: 'Do you want to save?',  
        showCancelButton: true,  
        confirmButtonText: `Yes`,  
        cancelButtonText: `No`,
        }).then((result) => {  
          if (result.isConfirmed) { 
            console.log("can submit");
            createCubeTestReport(cookies, {
              plant_id:inputs.plant_id,
              customer_id:SelectedDcGroup.customer_id,
              consignee_id:SelectedDcGroup.consignee_id,
              report_no:inputs.csr_no,
              report_date:getDisplayDate(inputs.csr_date),
              prefix:inputs.prefix,
              casting_dt:getDisplayDate(inputs.casting_dt),
              technician_name:inputs.technician_name?inputs.technician_name:'',
              cement_type:inputs.cement_type?inputs.cement_type:'',
              cured_place: resolveCuredPlaceFromAgeRows(rowsData, inputs.cured_place) || '',
              cement_content:inputs.cement_content?inputs.cement_content:'',
              grade:SelectedDcGroup.product_id,
              no_of_moulds:inputs.no_of_moulds?parseInt(inputs.no_of_moulds):0,
              user_remarks:inputs.user_remarks,
              dc_ids_list:SelectedDcGroup.dc_ids_list,
              supplied_qty:SelectedDcGroup.total_dc_qty,
              report_list:csrRows.map(q=>({
                age:parseInt(q.age),
                age_date:getDisplayDate(q.testing_date),
                sample_id:q.cube_id,
                act_weight:parseFloat(q.act_weight)?parseFloat(q.act_weight):0,
                act_load:parseFloat(q.act_load)?parseFloat(q.act_load):0,
                act_strength:parseFloat(q.act_strength)?parseFloat(q.act_strength):0,
                act_average:parseFloat(q.act_average)?parseFloat(q.act_average):0,
                act_difference:parseFloat(q.act_difference)?parseFloat(q.act_difference):0,
                act_result:(q.act_result)?(q.act_result):0,
                cus_weight:parseFloat(q.cus_weight)?parseFloat(q.cus_weight):0,
                cus_load:parseFloat(q.cus_load)?parseFloat(q.cus_load):0,
                cus_strength:parseFloat(q.cus_strength)?parseFloat(q.cus_strength):0,
                cus_average:parseFloat(q.cus_average)?parseFloat(q.cus_average):0,
                cus_difference:parseFloat(q.cus_difference)?parseFloat(q.cus_difference):0,
                cus_result:(q.cus_result),
                user_remarks:q.remarks_dtl,
              }))
            })
            .then(response => {
                Swal.fire("Saved!", "", "success");
                Reload();
            }).catch((error) => {
                displayError(error.response.data,"Saved Failed");
            });
          } 
          else if (result.isDismissed) 
          {    
            Swal.fire('Not saved', '', 'info')  
          }
        });
    }
    else{
      Swal.fire('No of moulds must be greater than 0', '', 'info')  
    }
  }
        
  const Reload = () => {
    window.location.reload();
  }
    
  const handleChange = (event) => {
    const name = event.target.name;
    const value = event.target.value;
    if (name === 'plant_id') {
      setInputs((values) => ({
        ...values,
        plant_id: value,
        casting_dt: '',
        dc_group_id: '',
      }));
      setSelectedDcGroup({});
      setRowsData([]);
      loadReportNumberForPlant(value);
      return;
    }
    setInputs((values) => ({ ...values, [name]: value }));
  }
  
  const Cancel = () => {
    setInputs(getInitialCubeTestReportFormState());
    setRowsData([]);
    setDcGroup([]);
    setSelectedDcGroup({});
    setCsrRows([]);
  }
   
  const view = () => {
    navigate('/CubeTestReportTable')
  } 

  const Back = () => {
    navigate('/Home')
  }

  const ClosePending = () => {
    setShowPendingModal(false);
  }

  const handleDcDetails = (event) => {
    const name = event.target.name;
    const value = event.target.value;
    if(name === "creation_date"){
        setInputs(values => ({...values, 'csr_date': event.target.value}))
    } 
    else {
        setInputs(values => ({...values, [name]: event.target.value}))
    }
  }
      
  const updateCsrNumberAndPrefix = (prefix, serial_no) => {
    setInputs(values => ({...values,
        ['csr_num_prefix']:(prefix+serial_no),
        ['csr_no']:serial_no,
        ['prefix']:prefix}));
  }

  return (
        <>
          <div id="outer-container" className="App" >
            <Sidebar pageWrapId={'page-wrap'} outerContainerId={'outer-container'} />
            <LoadingOverlay inProgress={inProgress}/>
            <div id="page-wrap">
              <form onSubmit={handleSubmit} >
                <div className="container">
                <PremiumPageHeader
                  module="QUALITY CONTROL"
                  title="Compressive Strength Report"
                  subtitle="Select plant and casting date, choose delivery challans, then enter cube test results."
                  modeLabel="ADD MODE"
                />
                </div>
                
                <FloatingControls tableLink="/CubeTestReportTable" onCancel={Cancel} enableCancel={true}/>
                <div className="container">
                    <CubeTestReportSelectionBanner selection={SelectedDcGroup} />
                    <Row>
                      <Col xl={6} lg={12} md={12}>
                        <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 bg-blue form-control-panel" >
                          <label htmlFor="plant_id" className="form-group col-sm-3 text-right">Plant Location</label>
                          <select id="plant_id" name="plant_id" className="form-control col-sm-7  browser-default custom-select   mandatory-form-control" required onChange={handleChange} value={inputs.plant_id || ""}>
                            <option value="">Select Plant</option>
                            {plants.map((item) => (
                              <option key={item.value} value={item.value}>
                                                  {item.label}</option>
                            ))}
                          </select><br />

                          <label className="form-group col-sm-3 text-right">Pending Tests</label>
                          <div className="form-group col-sm-7" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <button type="button" className="btn btn-primary btn-sm" onClick={() => openPendingList(7)}>
                              7 Days Pending
                            </button>
                            <button type="button" className="btn btn-primary btn-sm" onClick={() => openPendingList(28)}>
                              28 Days Pending
                            </button>
                          </div>
                          <br/>

                          <label htmlFor="casting_dt" className="form-group col-sm-3 text-right">Casting Date</label>
                          <input
                            id="casting_dt"
                            type="date"
                            className="form-control col-sm-7 mandatory-form-control"
                            name="casting_dt"
                            required
                            readOnly
                            title="Auto-filled when you select a row from the pending list"
                            style={{ backgroundColor: inputs.casting_dt ? '#fff' : '#f5f5f5', cursor: 'not-allowed' }}
                            value={inputs.casting_dt || ""}
                          /><br/>
                    
                          <div className="modal" tabIndex="-1" style={{ display: showPendingModal ? 'block' : 'none' }}>
                            <div className="modal-dialog modal-xl" style={{height: "500px"}} >
                              <div className="modal-content">
                                  <div className="modal-body display">
                                    <div className="container item-list-table-container">
                                      <ReactTablePopUpSubmitWrapper
                                          title={pendingModalTitle || 'Pending Cube Tests'}
                                          columns={pendingColumns}
                                          data={dcGroup}
                                          onRowSubmit={handlePendingRowSelect}
                                          isLoading={isLoading}
                                          onClose={ClosePending}
                                      />
                                    </div> 
                                  </div>
                              </div>
                            </div>
                          </div>

                          <label htmlFor="cement_type" className="form-group col-sm-3 text-right">Cement Type</label>
                          <input type="text" className="form-control col-sm-7"  id="cement_type" name="cement_type"  onChange={handleChange} value={inputs.cement_type || ""}  /><br />
                        
                          <label htmlFor="cement_content" className="form-group col-sm-3 text-right">Cem Content</label>
                          <input type="text" className="form-control col-sm-7"   id="cement_content" name="cement_content"   onChange={handleChange} value={inputs.cement_content || ""} /><br/>
                          
                          <label htmlFor="technician_name" className="form-group col-sm-3 text-right">Technician Name</label>
                          <input type="text" className="form-control col-sm-7" name="technician_name" id="technician_name"  onChange={handleChange} value={inputs.technician_name || ""} /><br />
                        </div>
                      </Col>
                  
                      <Col xl={6} lg={12} md={12}>
                        <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 bg-blue form-control-panel" >
                          <NumberSetting 
                            handleDateChange={handleDcDetails} 
                            serial_no={inputs.csr_no} 
                            creation_date={inputs.csr_date}
                            creation_time={inputs.csr_time}
                            prefix={inputs.prefix}
                            company_id={inputs.plant_id}
                            voucher_type={"compressive_strength_report"}
                            handleNumberAndPrefixUpdate={updateCsrNumberAndPrefix}
                            cookies={cookies}
                            serial_no_title={'Report No'}
                            hideTime={true}/>
                        </div>
                      </Col>
                    </Row>

                    <CubeTestReportAgePanel
                        visible={agePanelVisible}
                        rowsData={rowsData}
                        noOfMouldsCasted={inputs.no_of_moulds}
                        onAddRow={addTableRows}
                        onDeleteRow={deleteTableRows}
                        onChangeRow={handletableChange}
                        onGenerate={generateCubeRows}
                      />

                    <div className="form-row  shadow p-2 my-2 border-secondary p-2 mb-3 bg-blue container table-responsive">
                      <table className="table-bordered table-responsive searchTable table order-list" id="myTable" style={{textAlign:"center",borderRadius:"10px",backgroundColor:"RGB(188,232,253)"}}>
                        <thead style={{backgroundColor:"#bce8fd"}}>
                          <tr>
                              <th   style={{verticalAlign:"middle"}}>Sl No</th>
                              <th  style={{verticalAlign:"middle", minWidth:CUBE_ID_COLUMN_MIN_WIDTH}}>Cube ID</th>
                              <th  style={{verticalAlign:"middle"}}>Age</th>
                              <th  style={{verticalAlign:"middle"}}>Testing Date</th>
                              <th  style={{verticalAlign:"middle"}}>Weight(gms)</th>
                              <th  style={{verticalAlign:"middle"}}>Dimension(mm)</th>
                              <th  style={{verticalAlign:"middle"}}>Area mm2</th>
                              <th  style={{verticalAlign:"middle"}}>Reference Value</th>
                              <th  style={{verticalAlign:"middle"}}>Max Load(kn)</th>
                              <th  style={{verticalAlign:"middle"}}>Strength(mpa)</th>
                              <th  style={{verticalAlign:"middle"}}>Average Strength(mpa)</th>
                              <th  style={{verticalAlign:"middle",display:"None"}}>Difference</th>
                              <th  style={{verticalAlign:"middle"}}>Customer Weight(gms)</th>
                              <th  style={{verticalAlign:"middle"}}>Customer Max Load(kn)</th>
                              <th  style={{verticalAlign:"middle"}}>Customer Strength(mpa)</th>
                              <th  style={{verticalAlign:"middle"}}>Customer Average Strength2(mpa)</th>
                              <th  style={{verticalAlign:"middle",display:"None"}}>Customer Difference</th>
                              <th  style={{verticalAlign:"middle"}}>Remarks</th>
                          </tr>
                        </thead>
                        <tbody>
                          <TableRowsCubeTestReport csrRows={csrRows}  handleChangeCsr={handleChangeCsr}   />
                        </tbody>
                      </table>
                    </div> 

                    <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 bg-blue form-control-panel" >
                      <label htmlFor="user_remarks" className="form-group col-sm-3 text-center">Remarks</label>
                      <textarea rows="2" cols="50" className="form-control col-sm-9" id="user_remarks" name="user_remarks" onChange={handleChange} value={inputs.user_remarks || ""}></textarea>
                    </div>  
                </div>
                    
              </form>
            </div>
          </div>
        </>
  );
}
export default CubeTestReport;
